import { useEffect, useId, useState } from "react";
import { ChevronDown } from "lucide-react";
import type { Me } from "../api.js";
import { useAuth } from "../auth.js";
import type { LessonBlockDetail } from "../booking/staff.js";
import { canUseCreditCorrectionConsole, sourceLabel } from "../booking/creditCorrectionModel.js";
import { useStudentCreditReads } from "../booking/StudentCreditReads.js";
import { paymentMethodLabel } from "../booking/methods.js";
import { formatMoney } from "../booking/money.js";
import { formatDate } from "../format.js";
import { Pagination } from "../Pagination.js";
import { CARD_RECORD_PAGE_SIZE } from "../paginationPageSizes.js";
import { useAsync } from "../useAsync.js";

export function canViewStudentOverviewCredits(me: Me | null): boolean {
  if (me?.booking.staff !== true) return false;
  return me.roles.includes("tutor") || canUseCreditCorrectionConsole(me.capabilities, me.roles.includes("owner"));
}

export function canViewStudentOverviewPayments(me: Me | null): boolean {
  return canViewStudentOverviewCredits(me) && me !== null && (
    me.capabilities.includes("payments.manage") || canUseCreditCorrectionConsole(me.capabilities, me.roles.includes("owner"))
  );
}

export function StudentOverviewCredits({ studentId, refreshRevision = 0 }: { studentId: string; refreshRevision?: number }) {
  const { me } = useAuth();
  if (!canViewStudentOverviewCredits(me)) return null;
  // A different student starts with a closed card and no previous student's data.
  return <StudentOverviewCreditsContent key={studentId} studentId={studentId} refreshRevision={refreshRevision} showPayments={canViewStudentOverviewPayments(me)} />;
}

function StudentOverviewCreditsContent({ studentId, refreshRevision, showPayments }: { studentId: string; refreshRevision: number; showPayments: boolean }) {
  const creditReads = useStudentCreditReads();
  const [open, setOpen] = useState(false);
  const [hasOpened, setHasOpened] = useState(false);
  const [page, setPage] = useState(1);
  const headingId = useId();
  const detailsId = useId();
  const credits = useAsync(async () => {
    const result = await creditReads.lessonCredits(studentId);
    if (result.portal_student_id !== studentId) {
      throw new Error("The credit response did not match this student.");
    }
    return result;
  }, [studentId, refreshRevision, creditReads]);
  const purchases = useAsync(async () => {
    if (!hasOpened) return null;
    const result = await creditReads.lessonBlocks(studentId, {
      view: "all", limit: CARD_RECORD_PAGE_SIZE, offset: (page - 1) * CARD_RECORD_PAGE_SIZE,
    });
    if (result.blocks.some((detail) => detail.block.portal_student_id !== studentId)) {
      throw new Error("The block response did not match this student.");
    }
    return { page, result };
  }, [studentId, hasOpened, page, refreshRevision, creditReads]);
  const tutors = useAsync(
    () => hasOpened ? creditReads.tutors() : Promise.resolve(null),
    [hasOpened, creditReads.tutors],
  );
  const creditData = credits.data;
  const purchaseData = !purchases.loading && !purchases.error && purchases.data?.page === page
    ? purchases.data.result : null;
  const lastPage = Math.max(1, Math.ceil((purchases.data?.result.total ?? 0) / CARD_RECORD_PAGE_SIZE));
  const recoveringPage = purchaseData !== null && page > lastPage;

  useEffect(() => {
    if (recoveringPage) setPage(lastPage);
  }, [recoveringPage, lastPage]);

  function refresh() {
    creditReads.invalidate();
    credits.reload();
    purchases.reload();
    if (tutors.error) tutors.reload();
  }

  return (
    <section className="panel student-record-credits" aria-labelledby={headingId}>
      <h2 id={headingId} className="student-record-credits-heading">
        <button
          className="student-record-credits-toggle"
          type="button"
          aria-expanded={open}
          aria-controls={detailsId}
          aria-label="Remaining credits: blocks and purchases"
          onClick={() => {
            setOpen(!open);
            setHasOpened(true);
          }}
        >
          <span className="student-record-credits-summary">
            <span className="student-record-credits-label">Remaining credits</span>
            <span className="student-record-credits-balance" aria-hidden="true">
              {creditData ? <><strong className="tabular">{creditData.total_available_credits}</strong><span>{creditData.total_available_credits === 1 ? "credit available" : "credits available"}</span></>
                : credits.loading ? <span className="small muted">Loading credits…</span>
                : <span className="small muted">Balance unavailable</span>}
            </span>
          </span>
          <span className="student-record-credits-prompt">
            <span>{open ? "Hide blocks and purchases" : "View blocks and purchases"}</span>
            <ChevronDown size={20} aria-hidden="true" />
          </span>
        </button>
      </h2>
      <p className="visually-hidden" role="status">
        {creditData ? `${creditData.total_available_credits} ${creditData.total_available_credits === 1 ? "credit" : "credits"} available.`
          : credits.loading ? "Loading remaining credits." : "Credit balance unavailable."}
      </p>
      {credits.loading && creditData && <p className="small muted student-record-credits-update">Updating credits…</p>}
      {credits.error && (
        <div className="student-record-credits-error">
          <p className="error" role="alert">{creditData ? "We could not refresh credits. Showing the last checked balance." : "We could not load this student’s remaining credits."}</p>
          <button type="button" className="quiet small" onClick={credits.reload}>Retry credits</button>
        </div>
      )}
      <div id={detailsId} hidden={!open} className="student-record-credits-details">
        {open && <>
          <div className="student-record-section-head">
            <div>
              <h3>Blocks and purchases</h3>
              <p className="small muted">Available credits can be used now. Expired, cancelled and refunded blocks are included below for reference.</p>
            </div>
            <button type="button" className="quiet small" onClick={refresh} disabled={credits.loading || purchases.loading}>Refresh</button>
          </div>
          {!purchases.error && (!purchaseData || recoveringPage) && <p className="empty" role="status">Loading blocks and purchases…</p>}
          {purchases.error && (
            <div className="student-record-credits-error">
              <p className="error" role="alert">We could not load this student’s blocks and purchases.</p>
              <button type="button" className="quiet small" onClick={purchases.reload}>Retry blocks and purchases</button>
            </div>
          )}
          {purchaseData && !recoveringPage && <>
            {purchaseData.total === 0 ? <p className="empty">No lesson blocks or purchases are recorded for this student yet.</p> : <>
              <p className="small muted">{purchaseData.total} {purchaseData.total === 1 ? "block or purchase" : "blocks and purchases"}</p>
              <div className="student-record-credit-blocks">
                {purchaseData.blocks.map((detail) => <CreditBlock
                  key={detail.block.id}
                  detail={detail}
                  showPayments={showPayments}
                  tutorName={tutors.loading ? "Loading…" : tutors.error ? "Unavailable"
                    : tutors.data?.tutors.find((tutor) => tutor.externalProviderId === detail.block.provider_id)?.displayName ?? "Not recorded"}
                />)}
              </div>
            </>}
          </>}
          <Pagination
            page={page}
            pages={Math.max(page, lastPage)}
            onPageChange={setPage}
            disabled={purchases.loading}
            ariaLabel="Student blocks and purchases pages"
          />
        </>}
      </div>
    </section>
  );
}

function CreditBlock({ detail, tutorName, showPayments }: { detail: LessonBlockDetail; tutorName: string; showPayments: boolean }) {
  const { block, product } = detail;
  const paid = block.source === "amelia" || block.source === "staff";
  const status = block.status === "requires_attention" ? "Needs attention"
    : block.status === "active" && detail.is_expired ? "Expired"
    : block.status.charAt(0).toUpperCase() + block.status.slice(1);

  return (
    <article className="student-record-credit-block" aria-label={`${product.display_name} block`}>
      <div className="student-record-credit-block-heading">
        <div>
          <h4>{product.display_name}</h4>
          <p className="small muted">{sourceLabel(block.source)}</p>
        </div>
        <span className={`tag${block.status === "requires_attention" ? " warn" : ""}`}>{status}</span>
      </div>
      <dl className="student-record-credit-block-facts">
        <div><dt>Tutor</dt><dd>{tutorName}</dd></div>
        <div><dt>Available now</dt><dd><strong>{detail.available_balance}</strong> {detail.available_balance === 1 ? "credit" : "credits"}</dd></div>
        <div><dt>{paid ? "Purchased" : "Credits added"}</dt><dd>{block.credits_purchased} {block.credits_purchased === 1 ? "credit" : "credits"}</dd></div>
        <div><dt>{paid ? "Purchase date" : "Added on"}</dt><dd>{formatDate(paid ? block.paid_at : block.created_at)}</dd></div>
        <div><dt>Expires</dt><dd>{block.expires_at ? formatDate(block.expires_at) : "No expiry"}{detail.is_expired ? " · expired" : ""}</dd></div>
        {paid && showPayments && <div><dt>Payment recorded</dt><dd>{formatMoney(block.payment_amount, block.payment_currency)} · {paymentMethodLabel(block.payment_method)}</dd></div>}
      </dl>
      {showPayments && detail.refunds.length > 0 && <div className="student-record-credit-block-refunds">
        <strong className="small">Recorded refunds</strong>
        <ul className="small">
          {detail.refunds.map((refund) => <li key={refund.id}>{formatMoney(refund.amount_refunded, refund.payment_currency)} · {refund.credits_refunded} {refund.credits_refunded === 1 ? "credit" : "credits"} · {formatDate(refund.refunded_at)}</li>)}
        </ul>
      </div>}
      {showPayments && block.notes && <p className="small student-record-credit-block-notes">{block.notes}</p>}
    </article>
  );
}
